import {
  SET_PROCESSED_STATS,
  SET_PROCESSED_STATS_STATUS,
  SET_PROCESSED_STATS_LAST_FETCHED,
  SET_PER_SERVER_PROCESSED_STATS,
  SET_PER_SERVER_PROCESSED_STATS_STATUS,
  SET_PER_SERVER_PROCESSED_STATS_LAST_FETCHED,
  RESET_STATE,
} from "@/store/mutation-types";
import CacheService from "@/services/CacheService";
import { RequestMethod } from "@/types/request-method";
import { TimeRange, TIME_RANGES } from "@/types/time-range";
import { ProcessedStatsState } from "@/types/processed-stats";
import { StatType } from "@/types/stats";
import { Series, SeriesPayload, DataStatus } from "@/types/common";
import { RootState } from "@/types/root-state";
import * as Msg from "@/config/notification";
import { setNotificationError } from "@/utils/notification";
import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import { generateChartSeries } from "@/utils/series";
import { getAllProcessedStatsNames, getChartIDsFor } from "@/utils/chart";
import { getTimeRange } from "@/utils/conversion";
import { LIFETIME_MARGIN, PROCESSED_STATS_LIFETIME, emptySeriesArray } from "@/config/series";
import { ProcessedStatsInitialState } from "@/store/state";
import Vue from "vue";

const namespaced = true;

const state: ProcessedStatsState = new ProcessedStatsInitialState();

const mutations: MutationTree<ProcessedStatsState> = {
  [SET_PROCESSED_STATS]: (state, payload: SeriesPayload) => {
    // Initial state with time ranges is generated, so
    // no need to check if a time range property exists
    Vue.set(state.processedStats[payload.timeRange], payload.chartID, payload.seriesArray);
  },
  [SET_PROCESSED_STATS_STATUS]: (state, payload: { timeRange: TimeRange; status: DataStatus }) => {
    Vue.set(state.processedStatsStatus, payload.timeRange, payload.status);
  },
  [SET_PROCESSED_STATS_LAST_FETCHED]: (state, payload: { timeRange: TimeRange; timestamp: number }) => {
    Vue.set(state.processedStatsLastFetched, payload.timeRange, payload.timestamp);
  },
  [SET_PER_SERVER_PROCESSED_STATS]: (state, payload: SeriesPayload) => {
    // Initial state with time ranges is generated, so
    // no need to check if a time range property exists
    Vue.set(state.perServerProcessedStats[payload.timeRange], payload.chartID, payload.seriesArray);
  },
  [SET_PER_SERVER_PROCESSED_STATS_STATUS]: (state, payload: { timeRange: TimeRange; status: DataStatus }) => {
    Vue.set(state.perServerProcessedStatsStatus, payload.timeRange, payload.status);
  },
  [SET_PER_SERVER_PROCESSED_STATS_LAST_FETCHED]: (state, payload: { timeRange: TimeRange; timestamp: number }) => {
    Vue.set(state.perServerProcessedStatsLastFetched, payload.timeRange, payload.timestamp);
  },
  [RESET_STATE]: state => {
    Object.assign(state, new ProcessedStatsInitialState());
  },
};

const actions: ActionTree<ProcessedStatsState, RootState> = {
  fetchPlotsByTimeRange({ dispatch }) {
    for (const timeRange of TIME_RANGES) {
      dispatch("fetchStats", timeRange);
    }
  },
  fetchPerServerPlotsByTimeRange({ dispatch }) {
    for (const timeRange of TIME_RANGES) {
      dispatch("fetchPerServerStats", timeRange);
    }
  },
  fetchStats({ commit, dispatch, state, rootState }, timeRange: TimeRange) {
    const valid = !!(
      state.processedStatsLastFetched[timeRange] &&
      state.processedStatsLastFetched[timeRange] + PROCESSED_STATS_LIFETIME - LIFETIME_MARGIN >= Date.now()
    );

    if (
      (state.processedStatsStatus[timeRange] === DataStatus.FETCHED && valid) ||
      state.processedStatsStatus[timeRange] === DataStatus.FETCHING
    ) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_PROCESSED_STATS_STATUS, {
      timeRange: timeRange,
      status: DataStatus.FETCHING,
    });
    commit(SET_PROCESSED_STATS_LAST_FETCHED, {
      timeRange: timeRange,
      timestamp: Date.now(),
    });

    const { startTime, endTime } = getTimeRange(rootState.timeRange, timeRange);

    const params = {
      names: getAllProcessedStatsNames().join(),
      startTime,
      endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/insight/processed_stats`, RequestMethod.GET, params)
      .then(response => {
        if (response.data === null) {
          dispatch("setEmptySeries", {
            timeRange: timeRange,
            statsMutation: SET_PROCESSED_STATS,
            statusMutation: SET_PROCESSED_STATS_STATUS,
            status: DataStatus.FETCHED,
          });
          return;
        }

        const seriesArray: Series[] = response.data;

        for (const chartID of getChartIDsFor(StatType.PROCESSED)) {
          const payload: SeriesPayload = {
            timeRange,
            chartID,
            seriesArray: generateChartSeries(seriesArray, chartID),
          };
          commit(SET_PROCESSED_STATS, payload);
        }

        commit(SET_PROCESSED_STATS_STATUS, {
          timeRange: timeRange,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PROCESSED_STATS_ERROR} for ${timeRange} (${error})`);

        dispatch("setEmptySeries", {
          timeRange: timeRange,
          statsMutation: SET_PROCESSED_STATS,
          statusMutation: SET_PROCESSED_STATS_STATUS,
          status: DataStatus.FAILED,
        });

        commit(SET_PROCESSED_STATS_LAST_FETCHED, {
          timeRange: timeRange,
          timestamp: 0,
        });
      });
  },
  fetchPerServerStats({ commit, dispatch, state, rootState }, timeRange: TimeRange) {
    const valid = !!(
      state.perServerProcessedStatsLastFetched[timeRange] &&
      state.perServerProcessedStatsLastFetched[timeRange] + PROCESSED_STATS_LIFETIME - LIFETIME_MARGIN >= Date.now()
    );

    if (
      (state.perServerProcessedStatsStatus[timeRange] === DataStatus.FETCHED && valid) ||
      state.perServerProcessedStatsStatus[timeRange] === DataStatus.FETCHING
    ) {
      // Data already fetched and data valid, or fetching. Do not fetch
      return;
    }

    // Data not fetched or invalid, let's fetch
    commit(SET_PER_SERVER_PROCESSED_STATS_STATUS, {
      timeRange: timeRange,
      status: DataStatus.FETCHING,
    });
    commit(SET_PER_SERVER_PROCESSED_STATS_LAST_FETCHED, {
      timeRange: timeRange,
      timestamp: Date.now(),
    });

    const { startTime, endTime } = getTimeRange(rootState.timeRange, timeRange);

    const params = {
      names: getAllProcessedStatsNames().join(),
      startTime,
      endTime,
    };

    const cacheID = rootState.activeCacheID;

    return CacheService.request(`caches/${cacheID}/insight/processed_stats/per_server`, RequestMethod.GET, params)
      .then(response => {
        if (response.data === null) {
          dispatch("setEmptySeries", {
            timeRange: timeRange,
            statsMutation: SET_PER_SERVER_PROCESSED_STATS,
            statusMutation: SET_PER_SERVER_PROCESSED_STATS_STATUS,
            status: DataStatus.FETCHED,
          });
          return;
        }

        const seriesArray: Series[] = response.data;

        for (const chartID of getChartIDsFor(StatType.PROCESSED)) {
          const payload: SeriesPayload = {
            timeRange,
            chartID,
            seriesArray: generateChartSeries(seriesArray, chartID),
          };
          commit(SET_PER_SERVER_PROCESSED_STATS, payload);
        }

        commit(SET_PER_SERVER_PROCESSED_STATS_STATUS, {
          timeRange: timeRange,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.PROCESSED_STATS_ERROR} per server for ${timeRange} (${error})`);

        dispatch("setEmptySeries", {
          timeRange: timeRange,
          statsMutation: SET_PER_SERVER_PROCESSED_STATS,
          statusMutation: SET_PER_SERVER_PROCESSED_STATS_STATUS,
          status: DataStatus.FAILED,
        });

        commit(SET_PER_SERVER_PROCESSED_STATS_LAST_FETCHED, {
          timeRange: timeRange,
          timestamp: 0,
        });
      });
  },
  resetState({ commit }) {
    commit(RESET_STATE);
  },
  setEmptySeries(
    { commit },
    payload: { timeRange: TimeRange; statsMutation: string; statusMutation: string; status: DataStatus },
  ) {
    const { timeRange, statsMutation, statusMutation, status } = payload;
    // set empty values for series' data. To show data unavailable, preventing indefinite loading
    for (const chartID of getChartIDsFor(StatType.PROCESSED)) {
      const payload: SeriesPayload = {
        timeRange: timeRange,
        chartID: chartID,
        seriesArray: emptySeriesArray(),
      };
      commit(statsMutation, payload);
    }

    commit(statusMutation, {
      timeRange: timeRange,
      status: status,
    });
  },
};

const getters: GetterTree<ProcessedStatsState, RootState> = {
  aggregateSeries: (state, getters, rootState) => (chartID: string, chartIndex: number): false | Series => {
    const series = state.processedStats[rootState.timeRange][chartID][chartIndex];
    if (series) {
      return series;
    }
    return false;
  },
  perServerSeries: (state, getters, rootState) => (chartID: string, chartIndex: number): false | Series => {
    const series = state.perServerProcessedStats[rootState.timeRange][chartID][chartIndex];
    if (series) {
      return series;
    }
    return false;
  },
  perServerSeriesName: (state, getters, rootState) => (chartID: string, chartIndex: number): string => {
    const tags = state.perServerProcessedStats[rootState.timeRange][chartID][chartIndex]?.tags;
    if (tags) {
      return tags.server;
    }
    return "";
  },
  // Loading strategy:
  // - if status = FETCHED, data is ready
  // - else if status = FAILED, no data available
  // - else loading (status = UNFETCHED or status = FETCHING)
  perServerSeriesStatus: (state, getters, rootState) => {
    const status = state.perServerProcessedStatsStatus[rootState.timeRange];
    if (status) {
      return status;
    }
    return DataStatus.UNFETCHED;
  },
  statLastHour: state => (chartID: string, statName: string): number | null | false => {
    // Use `LAST_WEEK` because it has one hour granularity
    const series = state.processedStats[TimeRange.LAST_WEEK][chartID][0];

    if (!series) {
      return false; // Loading for use with BaseStats. null is a valid value
    }

    const points = series.values;

    if (points.length < 1) {
      return null; // no data/unavailable
    }

    const lastPoint = points[points.length - 1];

    const lastTime = new Date(lastPoint[0]);

    const timeLimit = new Date();
    timeLimit.setMinutes(timeLimit.getMinutes() - 90);

    // Most recent stat is invalid if older than 1 hour
    if (lastTime < timeLimit) {
      return null;
    }

    return lastPoint[series.columns.indexOf(statName)];
  },
};

export const processedStats: Module<ProcessedStatsState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
